import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Signs a path in the PRIVATE `hub-avatars` bucket on demand.
 *
 * LR Hub only. Separate from `src/lib/avatars/useAvatarUrl.ts` (core account
 * `avatars` bucket) on purpose — the two identity systems must never share a
 * bucket or a cache.
 */

const TTL_SECONDS = 60 * 60;
const REFRESH_WINDOW_MS = 5 * 60 * 1000;

type CacheEntry = { url: string; expiresAt: number };
const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<string | null>>();

async function signHubPath(path: string): Promise<string | null> {
  const existing = inflight.get(path);
  if (existing) return existing;
  const p = (async () => {
    const { data, error } = await supabase.storage
      .from("hub-avatars")
      .createSignedUrl(path, TTL_SECONDS);
    if (error || !data?.signedUrl) return null;
    cache.set(path, { url: data.signedUrl, expiresAt: Date.now() + TTL_SECONDS * 1000 });
    return data.signedUrl;
  })().finally(() => inflight.delete(path));
  inflight.set(path, p);
  return p;
}

export function useHubAvatarUrl(path?: string | null): { url: string | null; loading: boolean } {
  const [url, setUrl] = useState<string | null>(() => {
    if (!path) return null;
    const hit = cache.get(path);
    if (hit && hit.expiresAt - Date.now() > REFRESH_WINDOW_MS) return hit.url;
    return null;
  });
  const [loading, setLoading] = useState<boolean>(!!path && !url);

  useEffect(() => {
    let cancelled = false;
    if (!path) {
      setUrl(null);
      setLoading(false);
      return;
    }
    const hit = cache.get(path);
    if (hit && hit.expiresAt - Date.now() > REFRESH_WINDOW_MS) {
      setUrl(hit.url);
      setLoading(false);
      return;
    }
    setLoading(true);
    signHubPath(path).then((next) => {
      if (cancelled) return;
      setUrl(next);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [path]);

  return { url, loading };
}

/** Drop a cached signed URL (after re-upload / approval changes). */
export function invalidateHubAvatar(path?: string | null) {
  if (path) cache.delete(path);
}

export default useHubAvatarUrl;
